import React from 'react';
import { MessageCircle, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();
  const whatsappNumber = '21620505585';
  const whatsappMessage = encodeURIComponent(t('whatsapp.message'));
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/11564474/pexels-photo-11564474.jpeg')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#606060]/95 via-[#606060]/75 to-[#606060]/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="max-w-3xl">
          {/* Rating Badge */}
          <div 
            data-testid="hero-rating-badge"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6 animate-fade-in-up"
          >
            <div className="flex items-center gap-0.5 sm:gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 sm:w-4 sm:h-4 ${
                    i < 4 ? 'text-[#00bfa5] fill-[#00bfa5]' : 'text-[#00bfa5]/50 fill-[#00bfa5]/50'
                  }`}
                />
              ))}
            </div>
            <span className="text-white text-xs sm:text-sm font-medium">
              4.5/5 - 28 {t('hero.rating')}
            </span>
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6 font-['Outfit'] tracking-tight animate-fade-in-up animation-delay-200"
          >
            {t('hero.headline')}
          </h1>

          {/* Subheadline */}
          <p
            data-testid="hero-subheadline"
            className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 max-w-2xl leading-relaxed animate-fade-in-up animation-delay-400"
          >
            {t('hero.subheadline')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in-up animation-delay-600">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button
                size="lg"
                data-testid="hero-cta-whatsapp"
                className="w-full bg-[#00bfa5] hover:bg-[#00a88f] text-white rounded-full px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 justify-center"
              >
                <MessageCircle className="w-5 h-5" />
                {t('hero.cta_primary')}
              </Button>
            </a>
            <a href="#fleet" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                data-testid="hero-cta-fleet"
                className="w-full border-2 border-white text-white hover:bg-white hover:text-[#606060] rounded-full px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg font-semibold transition-all flex items-center gap-2 justify-center bg-transparent"
              >
                {t('hero.cta_secondary')}
                <ChevronRight className="w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Hidden on mobile */}
      <div className="hidden sm:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-white/80 rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
